import { makeAutoObservable } from 'mobx'
import { numberFormat } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type BillingPlan } from 'entities/Billing/model/BillingPlan'
import { openIntercom } from 'entities/Intercom'
import { PlanSelectionStore } from 'pages/settings/ui/BillingPlan/store/planSelectionStore'
import { type IPlan } from 'pages/settings/ui/BillingPlan/ui/PlanContainer'
import type { ISelectedPlanInfo } from 'pages/settings/ui/BillingPlan/store/plansSelectorStore'

export class ChoosePlanStore {
  private _selectedPlanType: 'Basic' | 'Pro' | null = null
  private _selectedPlan: BillingPlan | null = null
  private _onPlanSelected: (() => void) | null = null
  private _startTrialChecked = false
  readonly planSelectionStore: PlanSelectionStore

  constructor(readonly entities: IEntitiesStore) {
    this.planSelectionStore = new PlanSelectionStore(entities, { hideLabelRight: true })
    makeAutoObservable(this)
  }

  get period() {
    return this.planSelectionStore.period
  }

  get franchiseInterval() {
    if (this.franchise && typeof this.franchise === 'object') {
      return this.franchise.client_plan.interval
    }
    return ''
  }

  get franchise() {
    return this.entities.organizationStore.franchise
  }

  get selectedValues() {
    return this.planSelectionStore.selectedValues
  }

  get additionalSeatPrice() {
    return this.planSelectionStore.additionalSeatPrice
  }

  get additionalNumbersPrice() {
    return this.planSelectionStore.additionalNumbersPrice
  }

  get plans(): IPlan[] {
    const store = this.planSelectionStore
    return [
      {
        name: 'Basic',
        icon: 'cube',
        description: 'For small companies that want to text their customers at scale.',
        selectItems: store.basicPlansDropdownItems,
        onSelectValue: (value) => store.setSelectedValue('Basic', String(value)),
        buttonProps: {
          onClick: () => this.handleSelectPlan('Basic'),
          text: 'Select plan',
          contained: 'secondary',
        },
        iconColor: 'var(--green-70)',
        showTooltip: true,
        price: store.formatPrice(store.selectedBasicPlan),
        suffix: store.getPriceSuffix(store.selectedBasicPlan),
      },
      {
        name: 'Pro',
        icon: 'atom',
        description: 'For teams that deliver best-in-class service through texting and calling.',
        selectItems: store.proPlansDropdownItems,
        onSelectValue: (value) => store.setSelectedValue('Pro', String(value)),
        buttonProps: {
          onClick: () => this.handleSelectPlan('Pro'),
          text: 'Select plan',
          contained: 'primary',
        },
        iconColor: 'var(--content-brand-primary)',
        showTooltip: true,
        price: store.formatPrice(store.selectedProPlan),
        suffix: store.getPriceSuffix(store.selectedProPlan),
      },
      {
        name: 'Enterprise',
        icon: 'diamond',
        description: 'For businesses where texting is a vital part of operations.',
        selectItems: [
          { id: 'monthly', label: 'Monthly' },
          { id: 'yearly', label: 'Yearly' },
        ],
        onSelectValue: () => {},
        buttonProps: {
          id: 'intercom-custom-plan_modal-button',
          text: 'Chat with sales',
          contained: 'secondary',
          onClick: () => openIntercom(),
        },
        iconColor: 'var(--pink-70)',
        showTooltip: false,
        showCustomCredits: true,
        isCustom: true,
        price: 'Contact for pricing',
        suffix: null,
      },
    ]
  }

  get selectedPlan() {
    return this._selectedPlan
  }

  get franchisePlan() {
    if (this.franchise && typeof this.franchise === 'object') {
      return this.franchise.client_plan.plan_id
    }
    return ''
  }

  get selectedPlanType() {
    return this._selectedPlanType
  }

  get franchisePlanInfo() {
    if (typeof this.franchise === 'boolean') return null

    const { client_plan, agency_plan } = this.franchise
    const isAnnual = client_plan.interval !== 'monthly'

    return {
      name: 'Custom Plan',
      credits: client_plan.credits,
      additionalCreditPrice: this.setNumberFormat(client_plan.additional_credit_amount),
      isAnnual,
      seats: client_plan.seats,
      totalPrice: this.setNumberFormat(agency_plan.price / 100, true),
    }
  }

  setNumberFormat = (value: number, isCurrency = false) => {
    return numberFormat({
      value,
      currency: isCurrency ? 'USD' : undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })
  }

  setSelectedValue = (planName: string, value: string) => {
    this.planSelectionStore.setSelectedValue(planName, value)
  }

  handleSetPeriod = (tab: { key: 'monthly' | 'annually' }) => {
    this.planSelectionStore.handleSetPeriod(tab)
  }

  syncSelectedPlanAfterPeriodChange = () => {
    if (!this._selectedPlanType) return

    const planId = this.planSelectionStore.selectedValues[this._selectedPlanType]
    const plans =
      this._selectedPlanType === 'Basic'
        ? this.planSelectionStore.getBasicPlans(this.planSelectionStore.period)
        : this.planSelectionStore.getProPlans(this.planSelectionStore.period)
    this._selectedPlan = plans.find((p) => p.id === planId) || null
  }

  get startTrialChecked() {
    return this._startTrialChecked
  }

  setStartTrialChecked = (value: boolean) => {
    this._startTrialChecked = value
  }

  get checkoutPlanData() {
    return this.planSelectionStore.previewPlanData
  }

  get isCheckoutPlanLoading() {
    return this.planSelectionStore.previewPlanLoading
  }

  get checkoutSelectedPlanInfo(): ISelectedPlanInfo | null {
    const plan = this._selectedPlan
    if (!plan) return null
    const planInfo = this.entities.billingStore.billingPlansStore.getPlanInfo(
      plan.id,
      plan.interval
    )
    if (!planInfo) return null
    return {
      id: plan.id,
      icon: planInfo.icon,
      name: plan.labelCreditsByIntervalAndPlanType,
      iconColor: planInfo.iconColor,
      credits: plan.credits,
      additionalCreditPrice: plan.additional_credit_price,
      interval: plan.interval,
    }
  }

  get checkoutAnnualSavings() {
    return this.planSelectionStore.getAnnualSavings(this._selectedPlanType)
  }

  get checkoutPlanLines() {
    return this.planSelectionStore.previewPlanLines
  }

  get checkoutTotalDueToday() {
    return this.planSelectionStore.previewTotalDueToday
  }

  get checkoutTotalPlanData() {
    return this.planSelectionStore.previewTotalPlanData
  }

  loadPlanData = () => {
    const planId = this._selectedPlan?.id
    if (!planId) return
    this.planSelectionStore.loadPreviewPlanData({ plan: planId, seats: 0, numbers: 0 })
  }

  handleCheckoutSetPeriod = (tab: { key: 'monthly' | 'annually' }) => {
    this.planSelectionStore.handleSetPeriod(tab)
    this.syncSelectedPlanAfterPeriodChange()
    this.loadPlanData()
    this.setStartTrialChecked(false)
  }

  resetCheckout = () => {
    this._startTrialChecked = false
    this.planSelectionStore.clearPreviewPlanData()
  }

  setOnPlanSelected = (callback: (() => void) | null) => {
    this._onPlanSelected = callback
  }

  handleSelectPlan = (planType: 'Basic' | 'Pro') => {
    const planId = this.planSelectionStore.selectedValues[planType]
    const plans =
      planType === 'Basic'
        ? this.planSelectionStore.getBasicPlans(this.planSelectionStore.period)
        : this.planSelectionStore.getProPlans(this.planSelectionStore.period)
    this._selectedPlanType = planType
    this._selectedPlan = plans.find((p) => p.id === planId) || null
    this._onPlanSelected?.()
  }
}
